import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  FluentProvider,
  TabList,
  Tab,
  TabValue,
  makeStyles,
  tokens,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerHeaderNavigation,
  DrawerFooter,
} from '@fluentui/react-components';
import {
  HomeRegular,
  PeopleRegular,
  CalendarRegular,
  PersonRegular,
  SignOutRegular,
  DocumentRegular,
  LineHorizontal3Regular,
  DismissRegular,
  TaskListAddRegular,
  StarRegular,
  FolderRegular,
  PaintBrushRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '../types/auth';
import UpdatesModal from './UpdatesModal';
import { useTheme } from '../contexts/ThemeContext';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    height: '64px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    alignItems: 'center',
    padding: '0 24px',
    gap: '24px',
    boxShadow: tokens.shadow4,
    '@media (max-width: 768px)': {
      padding: '0 16px',
      gap: '16px',
    },
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '20px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    minWidth: '200px',
    '@media (max-width: 768px)': {
      fontSize: '16px',
      minWidth: 'auto',
      flex: 1,
    },
  },
  logoImage: {
    height: '28px',
    '@media (max-width: 768px)': {
      height: '20px',
    },
  },
  navigation: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  mobileMenuButton: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
    },
  },
  roleToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '180px',
  },
  roleLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightMedium,
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: '200px',
    justifyContent: 'flex-end',
    '@media (max-width: 768px)': {
      minWidth: 'auto',
      gap: '8px',
    },
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  mainContent: {
    flex: 1,
    padding: '24px',
    overflow: 'auto',
    '@media (max-width: 768px)': {
      padding: '16px',
    },
  },
  mobileDrawer: {
    '@media (min-width: 769px)': {
      display: 'none',
    },
  },
  mobileNavItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '8px',
    transition: 'all 0.2s',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  mobileNavIcon: {
    width: '20px',
    height: '20px',
  },
  mobileNavText: {
    fontSize: '16px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  mobileUserSection: {
    padding: '16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    marginTop: 'auto',
  },
  mobileUserInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  mobileUserDetails: {
    flex: 1,
  },
  mobileUserName: {
    fontSize: '16px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
  },
  mobileUserRole: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
});

const Layout: React.FC = () => {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasRole } = useAuth();
  const { theme, themeName, setTheme } = useTheme();

  const [selectedTab, setSelectedTab] = React.useState<TabValue>('home');
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isUpdatesModalOpen, setIsUpdatesModalOpen] = useState(false);

  const [isFirstTimeViewing, setIsFirstTimeViewing] = useState(false);

  // Check if user has seen the updates modal on first login
  useEffect(() => {
    const hasSeenUpdates = localStorage.getItem('hasSeenLatestUpdates');
    if (!hasSeenUpdates && user) {
      // Show modal after a short delay to ensure page is loaded
      setIsFirstTimeViewing(true);
      const timer = setTimeout(() => {
        setIsUpdatesModalOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Listen for custom event to open updates modal from Home page
  useEffect(() => {
    const handleOpenUpdates = () => {
      setIsUpdatesModalOpen(true);
    };
    window.addEventListener('openUpdatesModal', handleOpenUpdates);
    return () => window.removeEventListener('openUpdatesModal', handleOpenUpdates);
  }, []);

  // Define navigation items based on user role
  const getNavigationItems = () => {
    if (!user) return [];

    const baseItems = [
      { value: 'home', label: 'Home', icon: HomeRegular, path: '/home' },
      { value: 'calendar', label: 'Calendar', icon: CalendarRegular, path: '/calendar' }
    ];

    // Items for scheduler and above
    const schedulerItems = hasRole(['admin', 'executive', 'manager', 'scheduler']) ? [
      { value: 'patient-care', label: 'Care', icon: DocumentRegular, path: '/patient-care' }
    ] : [];

    // Items for manager and above
    const managerItems = hasRole(['admin', 'executive', 'manager']) ? [
      { value: 'tasks', label: 'Task Management', icon: TaskListAddRegular, path: '/tasks' }
    ] : [];

    // Items for executive and admin only
    const executiveItems = hasRole(['admin', 'executive']) ? [
      { value: 'documents', label: 'Document Management', icon: FolderRegular, path: '/documents' }
    ] : [];

    return [...baseItems, ...schedulerItems, ...managerItems, ...executiveItems];
  };

  const getVolunteerItems = () => {
    if (!user) return [];

    const volunteerItems = [
      { value: 'volunteers', label: 'Volunteer Roster', icon: PeopleRegular, path: '/volunteers' }
    ];

    // Add Scheduling Portal for scheduler and above
    if (hasRole(['admin', 'executive', 'manager', 'scheduler'])) {
      volunteerItems.push({ value: 'volunteer-list', label: 'Scheduling Portal', icon: CalendarRegular, path: '/volunteer-list' });
    }

    return volunteerItems;
  };

  const navigationItems = getNavigationItems();
  const volunteerItems = getVolunteerItems();

  React.useEffect(() => {
    // Set the selected tab based on current location
    const currentTab = navigationItems.find(item => item.path === location.pathname) ||
                      volunteerItems.find(item => item.path === location.pathname);
    if (currentTab) {
      setSelectedTab(currentTab.value);
    }
  }, [location.pathname, navigationItems, volunteerItems]);

  const handleTabSelect = (event: any, data: any) => {
    const selectedItem = navigationItems.find(item => item.value === data.value) ||
                        volunteerItems.find(item => item.value === data.value);
    if (selectedItem) {
      setSelectedTab(data.value);
      navigate(selectedItem.path);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const getRoleDisplayName = (role: UserRole) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'executive': return 'Executive Board';
      case 'manager': return 'Manager';
      case 'scheduler': return 'Scheduling Team';
      case 'onboarding': return 'Onboarding/Compliance';
      case 'volunteer': return 'Volunteer';
      default: return role;
    }
  };

  return (
    <FluentProvider theme={theme}>
      <div className={styles.root}>
        <header className={styles.header}>
          <div className={styles.logo}>
            <img src="/color-trucare.jpg" alt="TruCare" className={styles.logoImage} />
            TruCare Portal
          </div>

          {/* Mobile Menu Button */}
          <Button
            appearance="subtle"
            icon={<LineHorizontal3Regular />}
            className={styles.mobileMenuButton}
            onClick={() => setIsMobileDrawerOpen(true)}
          />

          {/* Desktop Navigation */}
          <nav className={styles.navigation}>
            <TabList
              selectedValue={selectedTab}
              onTabSelect={handleTabSelect}
              appearance="subtle"
            >
              {/* Home Tab */}
              <Tab value="home" icon={<HomeRegular />}>Home</Tab>

              {/* Volunteers Dropdown */}
              <Menu>
                <MenuTrigger>
                  <Button appearance="subtle" icon={<PeopleRegular />}>Volunteers</Button>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem
                      icon={<PeopleRegular />}
                      onClick={() => handleTabSelect(null, { value: 'volunteers' })}
                    >
                      Volunteer Roster
                    </MenuItem>
                    {hasRole(['admin', 'executive', 'manager', 'scheduler']) && (
                      <MenuItem
                        icon={<CalendarRegular />}
                        onClick={() => handleTabSelect(null, { value: 'volunteer-list' })}
                      >
                        Scheduling Portal
                      </MenuItem>
                    )}
                    <MenuItem
                      icon={<PeopleRegular />}
                      onClick={() => navigate('/org-chart')}
                    >
                      Org Chart
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>

              {/* Other navigation items */}
              {navigationItems.filter(item =>
                item.value !== 'home'
              ).map((item) => (
                <Tab
                  key={item.value}
                  value={item.value}
                  icon={<item.icon />}
                >
                  {item.label}
                </Tab>
              ))}
            </TabList>
          </nav>

          {/* Desktop User Section */}
          <div className={styles.userSection}>
            {user && (
              <>
                <div className={styles.userInfo}>
                  <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                    {getRoleDisplayName(user.role)}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: tokens.fontWeightMedium }}>
                    {user.name}
                  </div>
                </div>
                <Button
                  appearance="subtle"
                  icon={<StarRegular />}
                  onClick={() => setIsUpdatesModalOpen(true)}
                  title="What's New"
                  style={{ minWidth: 'auto', padding: '8px' }}
                />
                <Menu>
                  <MenuTrigger>
                    <Avatar
                      name={user.name}
                      size={32}
                      color="brand"
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem icon={<PersonRegular />}>
                        Profile
                      </MenuItem>
                      <MenuDivider />
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <MenuItem icon={<PaintBrushRegular />}>
                            Theme: {themeName.charAt(0).toUpperCase() + themeName.slice(1)}
                          </MenuItem>
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            <MenuItem 
                              onClick={() => setTheme('light')}
                              icon={themeName === 'light' ? <CheckmarkRegular /> : undefined}
                            >
                              Light
                            </MenuItem>
                            <MenuItem 
                              onClick={() => setTheme('dark')}
                              icon={themeName === 'dark' ? <CheckmarkRegular /> : undefined}
                            >
                              Dark
                            </MenuItem>
                            <MenuItem 
                              onClick={() => setTheme('red')}
                              icon={themeName === 'red' ? <CheckmarkRegular /> : undefined}
                            >
                              Red
                            </MenuItem>
                            <MenuItem 
                              onClick={() => setTheme('blue')}
                              icon={themeName === 'blue' ? <CheckmarkRegular /> : undefined}
                            >
                              Blue
                            </MenuItem>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                      <MenuDivider />
                      <MenuItem icon={<SignOutRegular />} onClick={handleSignOut}>
                        Sign Out
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </>
            )}
          </div>
        </header>

        <main className={styles.content}>
          <div className={styles.mainContent}>
            <Outlet />
          </div>
        </main>

        {/* Mobile Navigation Drawer */}
        <Drawer
          type="overlay"
          open={isMobileDrawerOpen}
          onOpenChange={(_, { open }) => setIsMobileDrawerOpen(open)}
          className={styles.mobileDrawer}
        >
          <DrawerHeader>
            <DrawerHeaderTitle>TruCare Portal</DrawerHeaderTitle>
            <DrawerHeaderNavigation>
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={() => setIsMobileDrawerOpen(false)}
              />
            </DrawerHeaderNavigation>
          </DrawerHeader>

          <DrawerBody>
            {/* Mobile Navigation Items */}
            <div className={styles.mobileNavItem} onClick={() => { handleTabSelect(null, { value: 'home' }); setIsMobileDrawerOpen(false); }}>
              <HomeRegular className={styles.mobileNavIcon} />
              <span className={styles.mobileNavText}>Home</span>
            </div>

            <div className={styles.mobileNavItem} onClick={() => { handleTabSelect(null, { value: 'volunteers' }); setIsMobileDrawerOpen(false); }}>
              <PeopleRegular className={styles.mobileNavIcon} />
              <span className={styles.mobileNavText}>Volunteer Roster</span>
            </div>

            {hasRole(['admin', 'executive', 'manager', 'scheduler']) && (
              <div className={styles.mobileNavItem} onClick={() => { handleTabSelect(null, { value: 'volunteer-list' }); setIsMobileDrawerOpen(false); }}>
                <CalendarRegular className={styles.mobileNavIcon} />
                <span className={styles.mobileNavText}>Scheduling Portal</span>
              </div>
            )}

            <div className={styles.mobileNavItem} onClick={() => { navigate('/org-chart'); setIsMobileDrawerOpen(false); }}>
              <PeopleRegular className={styles.mobileNavIcon} />
              <span className={styles.mobileNavText}>Org Chart</span>
            </div>

            {navigationItems.filter(item => item.value !== 'home').map((item) => (
              <div key={item.value} className={styles.mobileNavItem} onClick={() => { handleTabSelect(null, { value: item.value }); setIsMobileDrawerOpen(false); }}>
                <item.icon className={styles.mobileNavIcon} />
                <span className={styles.mobileNavText}>{item.label}</span>
              </div>
            ))}
          </DrawerBody>

          {/* Mobile User Section */}
          {user && (
            <DrawerFooter>
              <div className={styles.mobileUserSection}>
                <div className={styles.mobileUserInfo}>
                  <Avatar
                    name={user.name}
                    size={40}
                    color="brand"
                  />
                  <div className={styles.mobileUserDetails}>
                    <div className={styles.mobileUserName}>{user.name}</div>
                    <div className={styles.mobileUserRole}>{getRoleDisplayName(user.role)}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    icon={<StarRegular />}
                    onClick={() => {
                      setIsUpdatesModalOpen(true);
                      setIsMobileDrawerOpen(false);
                    }}
                    appearance="subtle"
                  >
                    What's New
                  </Button>
                  <Button
                    icon={<SignOutRegular />}
                    onClick={handleSignOut}
                    appearance="subtle"
                  >
                    Sign Out
                  </Button>
                </div>
              </div>
            </DrawerFooter>
          )}
        </Drawer>

        {/* Updates Modal */}
        <UpdatesModal 
          isOpen={isUpdatesModalOpen} 
          onClose={() => {
            setIsUpdatesModalOpen(false);
            setIsFirstTimeViewing(false);
          }}
          isFirstTime={isFirstTimeViewing}
        />
      </div>
    </FluentProvider>
  );
};

export default Layout; 